import {
  NAME_MAX_LENGTH,
  type PlayerProfile,
  type PlayerSkin,
  randomProfile,
  sanitizeName,
} from '@cube-rush/shared';
import { useState } from 'preact/hooks';
import { useApp } from '../../app/context';
import { ru } from '../../i18n/ru';
import { PAD_START_ATTR } from '../../input/focusNav';
import { cubeAccent } from '../../render/icons';
import { Avatar } from '../components/Avatar';
import { Button } from '../components/Button';
import { AvatarPicker, ColorPicker, IconPicker, TrailPicker } from '../profile/pickers';
import { SkinShowcase } from '../skin/SkinShowcase';
import './ProfileScreen.css';

/** A gamepad's focus starts on the tabs: neither ✕ nor a tile that would change the look. */
const padStart = { [PAD_START_ATTR]: '' };

type Tab = 'avatar' | 'icon' | 'colors' | 'trail';

const TABS: ReadonlyArray<{ id: Tab; emoji: string }> = [
  { id: 'avatar', emoji: '😺' },
  { id: 'icon', emoji: '🟩' },
  { id: 'colors', emoji: '🎨' },
  { id: 'trail', emoji: '✨' },
];

/** Character editor: name, avatar, cube icon, colours and trail with a live preview. */
export function ProfileScreen() {
  const app = useApp();
  const stored = app.session.profile.value;
  const mode = app.ui.profileEditor.value ?? 'edit';
  if (!stored) return null;
  return <ProfileEditor initial={stored} canCancel={mode === 'edit'} />;
}

function ProfileEditor({ initial, canCancel }: { initial: PlayerProfile; canCancel: boolean }) {
  const app = useApp();
  const [draft, setDraft] = useState(initial);
  const [tab, setTab] = useState<Tab>('avatar');

  const setSkin = (patch: Partial<PlayerSkin>) =>
    setDraft((current) => ({ ...current, skin: { ...current.skin, ...patch } }));

  const randomize = () =>
    setDraft((current) => ({ ...randomProfile(current.name || initial.name), name: current.name }));

  const save = () => {
    const name = sanitizeName(draft.name);
    if (name === '') {
      app.toasts.show(ru.profile.nameRequired, { tone: 'warning' });
      return;
    }
    app.saveProfile({ ...draft, name });
    app.ui.closeProfileEditor();
  };

  return (
    <main class="screen profile">
      <header class="profile__header">
        <h1 class="profile__title title-text">{ru.profile.title}</h1>
        {canCancel && (
          <button
            type="button"
            class="profile__cancel"
            aria-label={ru.profile.cancel}
            onClick={() => app.ui.closeProfileEditor()}
          >
            ✕
          </button>
        )}
      </header>

      <section class="profile__preview panel">
        <div class="profile__stage">
          <SkinShowcase skin={draft.skin} label={ru.profile.preview} />
        </div>
        <div class="profile__identity">
          <Avatar avatar={draft.avatar} size="big" ring={cubeAccent(draft.skin)} />
          <label class="field profile__name">
            <span class="field__label">{ru.profile.nameLabel}</span>
            <input
              class="field__input"
              value={draft.name}
              maxLength={NAME_MAX_LENGTH}
              autoComplete="nickname"
              autoCapitalize="words"
              enterKeyHint="done"
              spellcheck={false}
              onInput={(e) => {
                const name = e.currentTarget.value;
                setDraft((current) => ({ ...current, name }));
              }}
              // The keyboard's "done" key puts the keyboard away: it covers the pickers.
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
              }}
            />
          </label>
        </div>
      </section>

      <section class="profile__picker panel">
        <div class="profile__tabs" role="tablist" {...padStart}>
          {TABS.map(({ id, emoji }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              class={`profile__tab${tab === id ? ' profile__tab--on' : ''}`}
              onClick={() => setTab(id)}
            >
              <span class="profile__tab-icon" aria-hidden="true">
                {emoji}
              </span>
              <span>{ru.profile.tabs[id]}</span>
            </button>
          ))}
        </div>
        {/* Keyed by tab: a new tab starts scrolled to the top. */}
        <div key={tab} class="profile__options" role="tabpanel">
          {tab === 'avatar' && (
            <AvatarPicker
              value={draft.avatar}
              onChange={(avatar) => setDraft((current) => ({ ...current, avatar }))}
            />
          )}
          {tab === 'icon' && (
            <IconPicker skin={draft.skin} onChange={(icon) => setSkin({ icon })} />
          )}
          {tab === 'colors' && (
            <>
              <ColorPicker
                title={ru.profile.primaryColor}
                value={draft.skin.primary}
                onChange={(primary) => setSkin({ primary })}
              />
              <ColorPicker
                title={ru.profile.secondaryColor}
                value={draft.skin.secondary}
                onChange={(secondary) => setSkin({ secondary })}
              />
            </>
          )}
          {tab === 'trail' && (
            <TrailPicker skin={draft.skin} onChange={(trail) => setSkin({ trail })} />
          )}
        </div>
      </section>

      <footer class="profile__footer">
        <Button
          variant="purple"
          icon="🎲"
          class="profile__random"
          aria-label={ru.profile.randomize}
          onClick={randomize}
        >
          {ru.profile.randomize}
        </Button>
        <Button variant="green" size="big" class="profile__save" onClick={save}>
          {ru.profile.save}
        </Button>
      </footer>
    </main>
  );
}
